import { BrowserRouter as Router, Routes, Route, Link, useLocation, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { initializeApp } from "firebase/app";
import { getAuth, applyActionCode, verifyPasswordResetCode, confirmPasswordReset } from "firebase/auth";
import { getFirestore, collection, addDoc, serverTimestamp } from "firebase/firestore";

import './fonts.css';
import Lottie from "lottie-react";
import animationData from "./assets/welcome-lottie.json";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import TermsAndConditions from "./pages/TermsAndConditions";
import ResetPasswordPage from "./pages/ResetPasswordPage";

import ContactUs from "./pages/ContactUs";
import { Colors } from "./constants/colors";

// ✅ Initialize Firebase
const app = initializeApp({
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_FIREBASE_APP_ID,
  measurementId: process.env.REACT_APP_FIREBASE_MEASUREMENT_ID,
});

const db = getFirestore(app);

function Navbar() {
  const [isOpen, setIsOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const toggleMenu = () => setIsOpen(!isOpen);

  return (
    <div style={{
      position: "fixed",
      top: 0,
      left: 0,
      right: 0,
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      padding: "10px 40px",
      backgroundColor: Colors.background.light, // ✅ Changed from transparent
      zIndex: 1000,
      boxShadow: "0 2px 4px rgba(0,0,0,0.1)" // optional: subtle shadow for depth
    }}>
      <Link to="/" style={{ textDecoration: "none" }}>
        <img src="/assets/naryi-logo.png" alt="Naryi Logo" style={{ height: 40, width: "auto" }} />
      </Link>

      {isMobile ? (
        <>
          <div style={{ display: "flex", flexDirection: "column", cursor: "pointer", gap: 4 }} onClick={toggleMenu}>
            <div style={{ width: 25, height: 3, backgroundColor: "#333" }}></div>
            <div style={{ width: 25, height: 3, backgroundColor: "#333" }}></div>
            <div style={{ width: 25, height: 3, backgroundColor: "#333" }}></div>
          </div>
          {isOpen && (
            <div style={{
              position: "absolute",
              top: 60,
              right: 20,
              backgroundColor: Colors.background.light,
              padding: 20,
              display: "flex",
              flexDirection: "column",
              gap: 15,
              borderRadius: 8,
              boxShadow: "0 4px 12px rgba(0,0,0,0.15)"
            }}>
              <Link to="/privacy" style={{ textDecoration: "none", color: "#333", fontWeight: 500, fontSize: 18 }} onClick={toggleMenu}>Privacy</Link>
              <Link to="/terms" style={{ textDecoration: "none", color: "#333", fontWeight: 500, fontSize: 18 }} onClick={toggleMenu}>Terms</Link>
              <Link to="/contact-us" style={{ textDecoration: "none", color: "#333", fontWeight: 500, fontSize: 18 }} onClick={toggleMenu}>Contact Us</Link>
            </div>
          )}
        </>
      ) : (
        <div style={{ display: "flex", gap: 20 }}>
          <Link to="/privacy" style={{ textDecoration: "none", color: "#333", fontFamily: "Retrograde", fontWeight: 400, fontSize: 16 }}>Privacy</Link>
          <Link to="/terms" style={{ textDecoration: "none", color: "#333", fontFamily: "Retrograde", fontWeight: 400, fontSize: 16 }}>Terms</Link>
          <Link to="/contact-us" style={{ textDecoration: "none", color: "#333", fontFamily: "Retrograde", fontWeight: 400, fontSize: 16 }}>Contact Us</Link>
        </div>
      )}
    </div>
  );
}


function Home() {
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [email, setEmail] = useState("");
  const [name, setName] = useState(""); // ✅ new state for name
  const [status, setStatus] = useState("");

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const handleJoinWaitlist = async (e) => {
    e.preventDefault();

    if (!email) {
      setStatus("Please enter a valid email.");
      return;
    }

    try {
      await addDoc(collection(db, "mailingList"), {
        email,
        name: name || "",          // ✅ save name
        source: "landingPage",
        timestamp: serverTimestamp()
      });

      setStatus("Yay! You’ve joined the waitlist!");
      setEmail("");
      setName("");                // ✅ clear name field
    } catch (error) {
      console.error("Error adding email:", error);
      setStatus("Oops! Something went wrong. Please try again.");
    }
  };

  return (
    <div
      style={{
        display: "flex",
        flexDirection: isMobile ? "column" : "row",
        alignItems: "center",
        justifyContent: "center",
        minHeight: "100vh",
        fontFamily: "Retrograde, system-ui, sans-serif",
        backgroundColor: Colors.background.light,
        color: "#333",
        textAlign: "center",
        padding: "80px 20px 20px 20px",
        gap: 20,
        flexWrap: "wrap",
      }}
    >
      {/* Left side - text + animation + form */}
      <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", minWidth: 300, maxWidth: isMobile ? "90%" : 500 }}>
        <h1 style={{ fontSize: isMobile ? 28 : 32, fontWeight: 700, marginBottom: 10 }}>Welcome to Naryi</h1>
        <p style={{ fontSize: isMobile ? 16 : 18, fontWeight: 400, marginBottom: 10 }}>
          Your must-have time management and productivity toolkit for college.
        </p>
        <p style={{ fontSize: isMobile ? 16 : 18, fontWeight: 400, marginBottom: 10 }}>
          Coming soon, stay tuned!
        </p>

        <div style={{ width: "100%", maxWidth: 500, marginTop: 30 }}>
          <Lottie animationData={animationData} loop autoplay style={{ width: "100%", height: "auto" }} />
        </div>

        {/* ✅ Join Waitlist Form */}
        <form
          onSubmit={handleJoinWaitlist}
          style={{ marginTop: 30, display: "flex", flexDirection: "column", alignItems: "center", gap: 10, width: "100%", maxWidth: 400 }}
        >
          <input
            type="text"
            placeholder="Your Name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            style={{
              padding: "12px 16px",
              borderRadius: 8,
              border: "1px solid #ccc",
              fontSize: 16,
              width: "100%",
              fontFamily: "Retrograde, system-ui, sans-serif",
            }}
          />
          <input
            type="email"
            placeholder="Email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            style={{
              padding: "12px 16px",
              borderRadius: 8,
              border: "1px solid #ccc",
              fontSize: 16,
              width: "100%",
              fontFamily: "Retrograde, system-ui, sans-serif",
            }}
          />
          <button
            type="submit"
            style={{
              backgroundColor: Colors.buttonImportantSecondary.light,
              color: "#fff",
              border: "none",
              padding: "12px 16px",
              borderRadius: 8,
              fontSize: 16,
              cursor: "pointer",
              width: "100%",
              fontFamily: "Retrograde, system-ui, sans-serif",
            }}
          >
            Join Waitlist
          </button>
          {status && <p style={{ marginTop: 8, color: "#555", fontSize: 14 }}>{status}</p>}
        </form>
      </div>

      {/* Right side - app mockup */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "center", minWidth: 300, maxWidth: isMobile ? "90%" : 500, marginTop: isMobile ? 20 : 0 }}>
        <img
          src="/assets/app-mockup.png"
          alt="Naryi app mockup"
          style={{ width: "100%", height: "auto", objectFit: "contain", filter: "drop-shadow(0 4px 8px rgba(0,0,0,0.15))" }}
        />
      </div>
    </div>
  );
}


function VerifyPage() {
  const location = useLocation();
  const auth = getAuth();

  const [status, setStatus] = useState("Checking link...");
  const [mode, setMode] = useState(null);
  const [oobCode, setOobCode] = useState(null);

  // Reset password states
  const [isResetValid, setIsResetValid] = useState(false);
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const m = params.get("mode");
    const code = params.get("oobCode");

    if (!m || !code) {
      setStatus("🚫 Invalid link.");
      return;
    }

    setMode(m);
    setOobCode(code);

    if (m === "verifyEmail") {
      setStatus("Verifying email...");
      applyActionCode(auth, code)
        .then(() => setStatus("✅ Email verified successfully! You can now close this page or return to the app."))
        .catch((err) => {
          console.error("Firebase verification error:", err);
          setStatus("❌ Invalid or expired verification link.");
        });
    } else if (m === "resetPassword") {
      setStatus("Checking password reset link...");
      verifyPasswordResetCode(auth, code)
        .then(() => {
          setIsResetValid(true);
          setStatus("Enter your new password");
        })
        .catch(() => setStatus("❌ This password reset link is invalid or expired."));
    } else {
      setStatus("🚫 Unknown mode.");
    }
  }, [location, auth]);

  const handleReset = async (e) => {
    e.preventDefault();
    if (newPassword.length < 6) {
      setStatus("Password must be at least 6 characters.");
      return;
    }
    if (newPassword !== confirmPassword) {
      setStatus("Passwords do not match.");
      return;
    }
    try {
      await confirmPasswordReset(auth, oobCode, newPassword);
      setStatus("✅ Your password has been reset! You may now close this page.");
      setIsResetValid(false);
    } catch (err) {
      console.error("Reset password error:", err);
      setStatus("❌ Failed to reset password. Your link may have expired.");
    }
  };

  return (
    <div
      style={{
        minHeight: "100vh",
        padding: "100px 20px 20px 20px",
        backgroundColor: Colors.background.light,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "flex-start",
        fontFamily: "Retrograde, system-ui, sans-serif",
        color: "#333",
        textAlign: "center",
        gap: 20,
      }}
    >
      <h1>{mode === "resetPassword" ? "Reset Password" : "Email Verification"}</h1>
      <p style={{ maxWidth: 400 }}>{status}</p>

      {mode === "resetPassword" && isResetValid && (
        <form
          onSubmit={handleReset}
          style={{
            marginTop: 20,
            display: "flex",
            flexDirection: "column",
            gap: 12,
            width: "100%",
            maxWidth: 350,
          }}
        >
          <input
            type="password"
            placeholder="New Password"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            style={{
              padding: 12,
              borderRadius: 8,
              border: "1px solid #ccc",
              fontFamily: "Retrograde",
              fontSize: 16,
            }}
          />
          <input
            type="password"
            placeholder="Confirm New Password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            style={{
              padding: 12,
              borderRadius: 8,
              border: "1px solid #ccc",
              fontFamily: "Retrograde",
              fontSize: 16,
            }}
          />
          <button
            type="submit"
            style={{
              marginTop: 10,
              backgroundColor: Colors.buttonImportantSecondary.light,
              padding: 12,
              color: "#fff",
              border: "none",
              borderRadius: 8,
              fontSize: 17,
              cursor: "pointer",
              fontFamily: "Retrograde",
            }}
          >
            Reset Password
          </button>
        </form>
      )}
    </div>
  );
}


function ActionRedirect() {
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const mode = params.get("mode");

    if (!mode) return;

    // Keep all query params
    const queryString = params.toString();

    if (mode === "verifyEmail") {
      navigate(`/verify?${queryString}`, { replace: true });
    } else if (mode === "resetPassword") {
      navigate(`/reset?${queryString}`, { replace: true });
    }
  }, [location, navigate]);

  return null; // nothing to render
}

// ✅ App Component
function App() {
  return (
    <Router>
      <Navbar />
      {/* <ActionRedirect /> */}
      <Routes>
          <Route path="/" element={<Home />} />
  <Route path="/verify" element={<VerifyPage />} /> {/* handles both modes */}
  <Route path="/privacy" element={<PrivacyPolicy />} />
  <Route path="/terms" element={<TermsAndConditions />} />
  <Route path="/contact-us" element={<ContactUs />} />
</Routes>
    </Router>
  );
}

export default App;
